import React, {
  useContext,
  useState,
  useEffect,
  useRef,
  FC,
  ChangeEvent,
} from "react"
import ReactDatePicker, { ReactDatePickerProps } from "react-datepicker"
import { Icon } from "source/shared/components"
import { WebBootContext } from "source/publishing/WebBoot"
import moment from "moment-timezone"
import { useCurrentOrganizationTimeZone } from "source/calendar/hooks/useCurrentOrganizationTimeZone"

interface WebBootContextType {
  currentOrganization: {
    attributes: {
      datetime_fmt_configuration: {
        dateFirst: boolean
      }
    }
  }
}

interface DatePickerProps
  extends Omit<ReactDatePickerProps, "selected" | "onChange" | "value"> {
  value?: string | Date | null
  onChange?: (event: { target: { value: string } }) => void
  showOnLoad?: boolean
  errorClass?: string
  maxCap?: number
  minCap?: number
  placeholder?: string
  popperClassName?: string
}

export const DatePicker: FC<DatePickerProps> = ({
  value,
  onChange,
  showOnLoad = false,
  errorClass,
  maxCap = 20,
  minCap = 120,
  placeholder,
  popperClassName,
  ...rest
}) => {
  const {
    currentOrganization: {
      attributes: { datetime_fmt_configuration },
    },
  } = useContext(WebBootContext) as WebBootContextType
  const inputRef = useRef<ReactDatePicker>(null)
  const timeZone = useCurrentOrganizationTimeZone()
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    value ? moment.tz(value, timeZone).toDate() : null,
  )
  const currentYear = new Date().getFullYear()
  const minDate = new Date(currentYear - minCap, 0, 1)
  const maxDate = new Date(currentYear + maxCap, 11, 31)

  useEffect(() => {
    if (showOnLoad && inputRef.current) {
      inputRef.current.setFocus()
    }
  }, [showOnLoad])

  const handleChange = (date: Date | null) => {
    setSelectedDate(date)
    if (onChange) {
      const localDate = date
        ? new Date(date.getFullYear(), date.getMonth(), date.getDate())
        : null
      const formattedDate = localDate
        ? moment(localDate).format("YYYY-MM-DD")
        : ""
      const fakeEvent = { target: { value: formattedDate } }
      onChange(fakeEvent)
    }
  }

  // Handle specific cases where user types dates with dashes
  // we want the raw value to avoid React datepicker UTC parsing
  const handleRawChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value

    // Handle YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      setTimeout(() => {
        const [year, month, day] = value.split("-").map(Number)
        const localDate = new Date(year, month - 1, day)
        setSelectedDate(localDate)
        if (onChange) {
          const formattedDate = moment(localDate).format("YYYY-MM-DD")
          const fakeEvent = { target: { value: formattedDate } }
          onChange(fakeEvent)
        }
      }, 0)
    }

    // Handle MM-DD-YYYY  and DD-MM-YYYY format
    if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
      setTimeout(() => {
        let localDate
        if (datetime_fmt_configuration.dateFirst) {
          const [day, month, year] = value.split("-").map(Number)
          localDate = new Date(year, month - 1, day)
        } else {
          const [month, day, year] = value.split("-").map(Number)
          localDate = new Date(year, month - 1, day)
        }

        setSelectedDate(localDate)
        if (onChange) {
          const formattedDate = moment(localDate).format("YYYY-MM-DD")
          const fakeEvent = { target: { value: formattedDate } }
          onChange(fakeEvent)
        }
      }, 0)
    }
  }

  return (
    <div className={`date-field ${errorClass || ""}`}>
      <span className="date-field__icon">
        <Icon symbol="general#calendar" title="Date" />
      </span>
      <ReactDatePicker
        ref={inputRef}
        selected={selectedDate}
        onChange={handleChange}
        onChangeRaw={handleRawChange}
        dateFormat={
          datetime_fmt_configuration.dateFirst ? "dd/MM/yyyy" : "MM/dd/yyyy"
        }
        minDate={minDate}
        maxDate={maxDate}
        showYearDropdown
        showMonthDropdown
        showPopperArrow={false}
        dropdownMode="select"
        wrapperClassName="w-100%"
        placeholderText={
          placeholder ||
          (datetime_fmt_configuration.dateFirst ? "DD/MM/YYYY" : "MM/DD/YYYY")
        }
        popperClassName={popperClassName ?? ""}
        {...rest}
      />
    </div>
  )
}
